/*
 * The composer's per-user settings (spec 12 §3.5 under R-L, I21: the registry's `core.`-prefixed
 * keys), read out of the model's settings slice — the bootstrap fills it and `ui.settings.changed`
 * moves it — so the composer follows a change made on another device.
 *
 * The keys, their types and their defaults are not repeated here: `core/store/selectors.ts` mirrors
 * `internal/core/settings/keys.go` for the whole application, and this file only names the entries
 * the composer reads and gives them the shape its components want.
 *
 * It is also their one **writer** (M12). The composer settings page changes all four, the toolbar's
 * "Edit as markdown" changes one, and both go through `setComposerSetting`: a key with two writers
 * is a key whose value depends on which screen was used last.
 */

import { useMemo } from "react"
import { writeAccountSetting } from "../shell/preferences.tsx"
import { settingOf, type CoreSettings, type SettingKey } from "../store/selectors.ts"
import { client, useModel } from "../store/store.ts"

/**
 * Every composer setting and the registry key it is stored under. This table is the mapping, so a
 * new composer setting is one line here and is then readable, writable and typed everywhere.
 */
export const COMPOSER_SETTING_KEYS = {
	/** Enter sends and Shift+Enter inserts a newline; inverted, the other way round (Q21). */
	enterSends: "core.composer.enter_sends",
	/** Whether the preview pane starts open. */
	previewOpen: "core.composer.preview_open",
	/** The markdown source view instead of the formatted editor (F-2 §6.2). */
	sourceMode: "core.composer.source_mode",
	/** The undo-send hold in seconds, one of the registry's `UNDO_SEND_CHOICES` (A19); 0 is none. */
	undoSendSeconds: "core.composer.undo_send_seconds",
} as const satisfies Record<string, SettingKey>

/** The name a component uses for one composer setting. */
export type ComposerField = keyof typeof COMPOSER_SETTING_KEYS

/** The settings as a component wants them: one field per key, in the type the registry declares. */
export type ComposerSettings = {
	[F in ComposerField]: CoreSettings[(typeof COMPOSER_SETTING_KEYS)[F]]
}

// The two keys a caller outside this file names — a test that seeds the settings map, and the
// editor's own "Edit as markdown". The others are reached through the fields above.
export const ENTER_SENDS_KEY = COMPOSER_SETTING_KEYS.enterSends
export const SOURCE_MODE_KEY = COMPOSER_SETTING_KEYS.sourceMode

const FIELDS = Object.keys(COMPOSER_SETTING_KEYS) as ComposerField[]

/** The composer's settings as a merged settings map carries them; every other key is ignored. */
export function composerSettingsFrom(values: Record<string, unknown>): ComposerSettings {
	const settings = {} as ComposerSettings
	for (const field of FIELDS) {
		// `settingOf` answers the type the registry declares for the key, which is this field's type
		// by the definition of `ComposerSettings` above — a fact the mapped type states and an index
		// over the union of all four fields cannot re-derive on its own.
		settings[field] = settingOf(values, COMPOSER_SETTING_KEYS[field]) as never
	}
	return settings
}

/** What the composer runs with when the account has set nothing: the registry's own defaults. */
export const DEFAULT_COMPOSER_SETTINGS: ComposerSettings = composerSettingsFrom({})

/** The current settings outside React — the outbox reads its hold from here per send. */
export function composerSettings(): ComposerSettings {
	return composerSettingsFrom(client.model.settings)
}

export function useComposerSettings(): ComposerSettings {
	const values = useModel((model) => model.settings)
	return useMemo(() => composerSettingsFrom(values), [values])
}

/**
 * Changes one setting for this account, everywhere, through core's one write path for a preference
 * (`writeAccountSetting`: the model first, the server after, a failure quiet).
 */
export function setComposerSetting<F extends ComposerField>(
	field: F,
	value: ComposerSettings[F],
): void {
	const key = COMPOSER_SETTING_KEYS[field]
	writeAccountSetting(client.api, key, value)
}

/** Whether this account writes in the markdown source view rather than the formatted editor. */
export function sourceMode(): boolean {
	return composerSettings().sourceMode
}

/** The toolbar's "Edit as markdown", and the source view's way back out of it. */
export function setSourceMode(on: boolean): void {
	setComposerSetting("sourceMode", on)
}
